import { useState } from 'react';
import { ModelVersion } from '@/types/models';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, RotateCcw, Eye, GitCommit } from 'lucide-react';

interface VersionHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: ModelVersion[];
  currentVersionId: string | null;
  onPreviewVersion: (version: ModelVersion) => void;
  onRestoreVersion: (version: ModelVersion) => Promise<void>;
  isRestoring?: boolean;
}

// Mock versions for UI development
const MOCK_VERSIONS: ModelVersion[] = [
  {
    id: 'v1',
    model_id: 'model-1',
    version_number: 3,
    scad_code: '// Version 3 code',
    commit_message: 'Added chamfered edges and improved button layout',
    created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 mins ago
  },
  {
    id: 'v2',
    model_id: 'model-1',
    version_number: 2,
    scad_code: '// Version 2 code',
    commit_message: 'Changed button style to round',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
  },
  {
    id: 'v3',
    model_id: 'model-1',
    version_number: 1,
    scad_code: '// Version 1 code',
    commit_message: 'Initial version',
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
  },
];

export function VersionHistory({
  open,
  onOpenChange,
  versions = MOCK_VERSIONS,
  currentVersionId,
  onPreviewVersion,
  onRestoreVersion,
  isRestoring = false,
}: VersionHistoryProps) {
  const [restoreTarget, setRestoreTarget] = useState<ModelVersion | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    await onRestoreVersion(restoreTarget);
    setRestoreTarget(null);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-[400px] border-border bg-background p-0"
        >
          <SheetHeader className="border-b border-border p-4">
            <SheetTitle className="flex items-center gap-2 text-foreground">
              <Clock className="h-5 w-5" />
              Version History
            </SheetTitle>
            <SheetDescription className="text-muted-foreground">
              {versions.length} version{versions.length !== 1 ? 's' : ''}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-100px)]">
            <div className="space-y-3 p-4">
              {versions.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <GitCommit className="mx-auto mb-3 h-12 w-12 opacity-50" />
                  <p>No versions yet</p>
                  <p className="mt-1 text-sm">
                    Save your model to create the first version
                  </p>
                </div>
              ) : (
                versions.map((version, index) => {
                  const isCurrent = version.id === currentVersionId;
                  const isLatest = index === 0;

                  return (
                    <div
                      key={version.id}
                      className={`relative rounded-lg border p-4 transition-colors ${
                        isCurrent
                          ? 'border-sky-500 bg-sky-500/10'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      {/* Version number badge */}
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded px-2 py-0.5 font-mono text-sm ${
                              isCurrent
                                ? 'bg-sky-500/20 text-sky-500'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            v{version.version_number}
                          </span>
                          {isLatest && (
                            <span className="rounded bg-green-500/20 px-2 py-0.5 text-xs text-green-400">
                              Latest
                            </span>
                          )}
                          {isCurrent && (
                            <span className="rounded bg-sky-500/20 px-2 py-0.5 text-xs text-sky-500">
                              Current
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(version.created_at)}
                        </span>
                      </div>

                      {/* Commit message */}
                      <p className="mb-3 text-sm text-muted-foreground">
                        {version.commit_message || (
                          <span className="italic opacity-50">
                            No commit message
                          </span>
                        )}
                      </p>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onPreviewVersion(version)}
                          className="h-7 px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          Preview
                        </Button>
                        {!isCurrent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRestoreTarget(version)}
                            className="h-7 px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Restore confirmation dialog */}
      <AlertDialog
        open={!!restoreTarget}
        onOpenChange={() => setRestoreTarget(null)}
      >
        <AlertDialogContent className="border-border bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Restore to version {restoreTarget?.version_number}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will create a new version with the code from version{' '}
              {restoreTarget?.version_number}. Your current changes will be
              preserved in the version history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={isRestoring}
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {isRestoring ? 'Restoring...' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
